// src/components/pages/admin-dash/PaymentsUpdate.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Icon from "../../ui/Icon";
import Badge from "../../ui/Badge";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { StatusBadge } from "./PaymentsListByFilter";

const STATUS_OPTIONS = [
  { value: "PROCESSING", label: "Processing" },
  { value: "PAID_OUT",   label: "Paid out"   },
  { value: "ERROR",      label: "Error"      },
];

const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
    : "—";

/**
 * Update a single payment by ID.
 *
 * Props:
 *   initialPaymentId   string  optional — auto-loads when present (used
 *                              when navigated to from the Overview's
 *                              pending-payments queue).
 *   onShowToast        fn
 */
export default function PaymentsUpdate({ initialPaymentId, onShowToast }) {
  const [paymentId, setPaymentId] = useState(initialPaymentId || "");
  const [payment, setPayment]     = useState(null);
  const [form, setForm]           = useState({ status: "PROCESSING", amount: 0 });
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [loadError, setLoadError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refunding, setRefunding] = useState(false);

  // Auto-load if id passed in
  useEffect(() => {
    if (initialPaymentId) {
      setPaymentId(String(initialPaymentId));
      handleLoad(initialPaymentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPaymentId]);

  const handleLoad = async (idArg) => {
    const id = idArg ?? paymentId;
    if (!id) return;
    setLoading(true);
    setLoadError("");
    setPayment(null);
    try {
      const res = await axiosInstance.get(`/admin/payment/${id}`);
      setPayment(res.data);
      setForm({ status: res.data.status || "PROCESSING", amount: res.data.amount ?? 0 });
    } catch (err) {
      setLoadError(
        err?.response?.status === 404
          ? "Payment not found."
          : "Failed to load payment details."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => e.key === "Enter" && handleLoad();

  const canRefund =
    payment &&
    payment.status === "PAID_OUT" &&
    (payment.transactionType === "ONE_TIME_PURCHASE" ||
      (!payment.transactionType && payment.creditPlan));

  const doRefund = async () => {
    setRefunding(true);
    try {
      await axiosInstance.post(`/admin/payment/${paymentId}/refund`);
      onShowToast?.("Payment refunded — credits and balance clawed back.", "success");
      await handleLoad();
      setRefundOpen(false);
    } catch (err) {
      onShowToast?.(
        err?.response?.data?.message || "Failed to refund payment.",
        "error"
      );
    } finally {
      setRefunding(false);
    }
  };

  const willChange =
    payment &&
    (form.status !== payment.status ||
      Number(form.amount) !== Number(payment.amount));

  const sensitiveStatusChange =
    payment &&
    payment.status !== form.status &&
    (form.status === "PAID_OUT" || form.status === "ERROR");

  const requestUpdate = (e) => {
    e?.preventDefault?.();
    if (!willChange) return;
    // For sensitive transitions, require confirmation; otherwise save directly.
    if (sensitiveStatusChange) {
      setConfirmOpen(true);
    } else {
      doSave();
    }
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/admin/payment/${paymentId}`, {
        status: form.status,
        amount: Number(form.amount),
      });
      onShowToast?.("Payment updated successfully.", "success");
      // Reload so the displayed payment reflects the new state.
      await handleLoad();
      setConfirmOpen(false);
    } catch (err) {
      onShowToast?.(
        err?.response?.data?.message || "Failed to update payment.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Update payment
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Load a payment by ID, then adjust its status and amount.
        </p>
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <Field
            label="Payment ID"
            type="number"
            value={paymentId}
            onChange={(e) => setPaymentId(e.target.value)}
            onKeyDown={handleKey}
            placeholder="e.g. 4218"
            className="!mb-0"
          />
          <div className="flex items-end">
            <Button icon="search" onClick={() => handleLoad()} loading={loading} disabled={!paymentId}>Load
            </Button>
          </div>
        </div>
      </Card>

      {loadError && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{
            background: "var(--red-dim)",
            border: "1px solid var(--red-border)",
            color: "var(--red)",
          }}
        >
          {loadError}
        </div>
      )}

      {payment && (
        <>
          <Card>
            <div className="lg-label mb-3">Current details</div>
            <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 md:grid-cols-3">
              <DetailField label="Payment ID" mono value={`#${payment.id}`} />
              <DetailField label="User"       value={`${payment.user?.name || "—"} · #${payment.user?.id || "?"}`} />
              <DetailField label="Plan"       value={payment.creditPlan} />
              <DetailField label="Method"     value={payment.paymentMethod} />
              <DetailField label="Txn ID"     mono value={payment.idTransaction} />
              <DetailField label="Date"       value={payment.date?.split("T")[0]} />
              <DetailField label="Current status"
                value={<StatusBadge status={payment.status} />} />
              <DetailField label="Current amount" value={fmtUsd(payment.amount)} />
            </div>

            {canRefund && (
              <div className="flex justify-end mt-4 pt-4" style={{ borderTop: "1px solid var(--border)" }}>
                <Button
                  variant="danger"
                  size="sm"
                  icon="ban"
                  onClick={() => setRefundOpen(true)}
                >
                  Refund payment
                </Button>
              </div>
            )}
          </Card>

          <Card>
            <form onSubmit={requestUpdate} className="space-y-1">
              <div className="lg-label mb-3">Update fields</div>
              <div className="grid gap-3 sm:grid-cols-2">
                <SelectFieldLabel
                  label="New status"
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, status: e.target.value }))
                  }
                  options={STATUS_OPTIONS}
                />
                <Field
                  label="New amount (USD)"
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.amount}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, amount: e.target.value }))
                  }
                  hint={
                    Number(form.amount) !== Number(payment.amount)
                      ? `Δ ${
                          Number(form.amount) > Number(payment.amount) ? "+" : ""
                        }${fmtUsd(Number(form.amount) - Number(payment.amount))}`
                      : undefined
                  }
                />
              </div>

              {willChange && (
                <div
                  className="rounded-xl px-3 py-2 my-2 text-[12px] flex items-center gap-2"
                  style={{
                    background: "var(--gold-dim)",
                    border: "1px solid var(--gold-border)",
                    color: "var(--txt-2)",
                  }}
                >
                  <Icon name="alert-circle" size={14} style={{ color: "var(--gold)" }} />
                  <span>
                    {form.status !== payment.status && (
                      <>
                        Status{" "}
                        <span style={{ color: "var(--txt)" }}>
                          {payment.status}
                        </span>{" "}
                        →{" "}
                        <span style={{ color: "var(--txt)" }}>{form.status}</span>
                      </>
                    )}
                    {form.status !== payment.status &&
                      Number(form.amount) !== Number(payment.amount) &&
                      " · "}
                    {Number(form.amount) !== Number(payment.amount) && (
                      <>
                        Amount{" "}
                        <span style={{ color: "var(--txt)" }}>
                          {fmtUsd(payment.amount)}
                        </span>{" "}
                        →{" "}
                        <span style={{ color: "var(--txt)" }}>
                          {fmtUsd(Number(form.amount))}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() =>
                    setForm({ status: payment.status, amount: payment.amount })
                  }
                  disabled={!willChange || saving}
                >
                  Reset
                </Button>
                <Button
                  size="sm"
                  type="submit"
                  loading={saving}
                  disabled={!willChange}
                >
                  Update payment
                </Button>
              </div>
            </form>
          </Card>
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title={form.status === "PAID_OUT" ? "Mark as paid out?" : "Mark as errored?"}
        message={
          form.status === "PAID_OUT"
            ? "Marking this payment as paid out is recorded in the audit log and may notify the user."
            : "Marking this payment as errored will flag it for follow-up."
        }
        confirmLabel={form.status === "PAID_OUT" ? "Mark paid out" : "Mark errored"}
        tone={form.status === "ERROR" ? "danger" : "default"}
        loading={saving}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doSave}
      />

      {payment && (
        <ConfirmDialog
          open={refundOpen}
          title="Refund this payment?"
          message={
            `This calls the real ${payment.paymentMethod} refund API for ${fmtUsd(payment.amount)} — ` +
            "it isn't just a local status change, and it can't be undone from here. " +
            `The ${payment.creditPlan ? `${payment.creditPlan} plan's` : ""} credits and balance already ` +
            "granted to the user will also be clawed back (down to zero if they've already spent some)."
          }
          confirmLabel="Refund payment"
          tone="danger"
          loading={refunding}
          onClose={() => setRefundOpen(false)}
          onConfirm={doRefund}
        />
      )}
    </div>
  );
}

function DetailField({ label, value, mono }) {
  return (
    <div>
      <div className="lg-label mb-1">{label}</div>
      <div
        className={`text-[13.5px] ${mono ? "tabular-nums" : ""}`}
        style={{ color: "var(--txt)", wordBreak: "break-word" }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function SelectFieldLabel({ label, value, onChange, options }) {
  return (
    <div className="mb-3.5">
      <label
        className="block text-[12px] font-medium mb-1.5 tracking-wide"
        style={{ color: "var(--txt)" }}
      >
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          className="w-full px-3.5 py-[11px] text-[14px] outline-none rounded-full border appearance-none cursor-pointer pr-9"
          style={{
            background: "var(--bg)",
            color: "var(--txt)",
            borderColor: "var(--border-2)",
          }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px]"
          style={{ color: "var(--txt-3)" }}
        >
          ▾
        </span>
      </div>
    </div>
  );
}
